import { sourceGroupLabel } from "./SourceGroupsCell";
import type { SourceGroupRef } from "./types";
// IR-1238: show/hide columns for the Join Links table, persisted per-browser in
// localStorage. The Link and Actions columns are always visible; everything else
// is toggleable. The six detail columns (source groups, risk, first/last seen,
// appearances, avatar finder) start hidden — opt-in per Tomer/Rotem's spec.

export type LinkColumnKey =
  | "target_name"
  | "target_picture"
  | "target_kind"
  | "platform"
  | "operational_status"
  | "approval_required"
  | "classification"
  | "target_bio"
  | "target_members"
  | "source_group_count"
  | "risk_score"
  | "first_seen"
  | "last_seen"
  | "appearances"
  | "avatar_finder";

// Picker menu order: mandatory (default-visible) block first, then the optional
// (default-hidden) block. This is the MENU order, not the table order.
export const LINK_COLUMNS: { key: LinkColumnKey; label: string }[] = [
  { key: "target_name", label: "Name" },
  // IR-1386 r3 (Rotem): Type precedes Picture here too, so the picker reads in
  // the same order as the table it controls.
  { key: "target_kind", label: "Type" },
  { key: "target_picture", label: "Picture" },
  { key: "platform", label: "Platform" },
  { key: "operational_status", label: "Operational Status" },
  { key: "approval_required", label: "Approval Required" },
  { key: "classification", label: "Classification" },
  { key: "target_bio", label: "Bio" },
  { key: "target_members", label: "Members" },
  { key: "source_group_count", label: "Source Groups" },
  { key: "risk_score", label: "Risk Score" },
  { key: "first_seen", label: "First Seen" },
  { key: "last_seen", label: "Last Seen" },
  { key: "appearances", label: "Appearances" },
  { key: "avatar_finder", label: "Avatar (finder)" },
];

export const DEFAULT_VISIBLE: LinkColumnKey[] = [
  "target_name",
  "target_kind",
  "target_picture",
  "platform",
  "operational_status",
  "approval_required",
  "classification",
  "target_bio",
  "target_members",
];

/**
 * IR-1233 r6 — the Source Groups column is default-HIDDEN (IR-1238), but the
 * backend search matches contributing source-group names (r2). A query that
 * hits only that column therefore returned rows with nothing highlighted
 * anywhere: Rotem's 2026-07-27 "CVE" report — 117 results, no match/highlight,
 * "no apparent reason". (Measured on dev: 29 matches, 3 in a visible column,
 * 26 only in the source group "GO-TO CVE Chat".) Because useMatchNav walks
 * RENDERED marks, those rows were also uncounted and unreachable.
 *
 * So while a search is active and any loaded row would show a matching chip,
 * the column is revealed automatically. The reveal is view-only — it is never
 * written to localStorage and never shown as selected in the Columns picker,
 * so the analyst's own choice survives the search.
 */
export const SOURCE_GROUPS_COLUMN: LinkColumnKey = "source_group_count";

/** Minimal row shape the auto-reveal decision needs. */
interface SourceGroupBearingRow {
  source_groups?: readonly SourceGroupRef[] | null;
}

/**
 * True when `q` matches a source-group name on any loaded row — i.e. the
 * Source Groups column, if shown, would render at least one highlighted chip.
 * Matches case-insensitively on `sourceGroupLabel`, the same string the chip
 * renders and `highlightMatches` marks.
 */
export function matchesHiddenSourceGroup(
  q: string | null | undefined,
  rows: readonly SourceGroupBearingRow[],
): boolean {
  const needle = q?.trim().toLowerCase();
  if (!needle) return false;
  return rows.some((r) =>
    (r.source_groups ?? []).some((g) => sourceGroupLabel(g).toLowerCase().includes(needle)),
  );
}

/**
 * The column set the table should actually render: the analyst's selection,
 * plus Source Groups when the active search matched only there.
 * `hideOverridden` is set once the analyst explicitly turns the column back off
 * during the same search, so the reveal never fights their choice.
 */
export function effectiveVisibleColumns(
  visibleCols: readonly LinkColumnKey[],
  q: string | null | undefined,
  rows: readonly SourceGroupBearingRow[],
  hideOverridden: boolean,
): LinkColumnKey[] {
  if (visibleCols.includes(SOURCE_GROUPS_COLUMN) || hideOverridden) return [...visibleCols];
  if (!matchesHiddenSourceGroup(q, rows)) return [...visibleCols];
  return [...visibleCols, SOURCE_GROUPS_COLUMN];
}

const STORAGE_KEY = "links-columns-v1";

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

export function loadVisibleColumns(storage: ReadableStorage = localStorage): LinkColumnKey[] {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE;
    const valid = new Set<string>(LINK_COLUMNS.map((c) => c.key));
    return parsed.filter((k): k is LinkColumnKey => typeof k === "string" && valid.has(k));
  } catch {
    return DEFAULT_VISIBLE;
  }
}

export function saveVisibleColumns(
  cols: LinkColumnKey[],
  storage: WritableStorage = localStorage,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(cols));
  } catch {
    // quota / private mode — persistence is best-effort
  }
}
